import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import firebaseConfig from '../firebase-config';
import { pnSubscribe, pnUnsubscribe } from '../networking/Api';
import { saveFcmToken, getFcmToken, clearFcmToken } from '../utils/Prefs';

const FCM_MIGRATION_DONE = 'fcm_migration_v1';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

/**
 * Request notification permission from the browser, get an FCM token,
 * and register it with the backend via pn/subscribe.
 * The caller is responsible for checking shouldAskNotificationPermission()
 * before calling this — this function does NOT check prompt count itself.
 *
 * @returns {Promise<string|null>} FCM token on success, null otherwise
 */
export const requestNotificationPermission = async () => {
    try {
        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
            return null;
        }

        const token = await getToken(messaging, {
            vapidKey: firebaseConfig.vapidKey
        });

        if (!token) {
            return null;
        }

        // One-time migration: unsubscribe any existing token from the old broken
        // topic (wrong Firebase project + 'esatsang-broadcast' hyphen), then
        // re-subscribe to the correct one. Runs once per browser via localStorage flag.
        if (!localStorage.getItem(FCM_MIGRATION_DONE)) {
            const existingToken = getFcmToken();
            if (existingToken) {
                await _unsubscribeFromBackend(existingToken);
            }
            clearFcmToken();
            localStorage.setItem(FCM_MIGRATION_DONE, '1');
        }

        const storedToken = getFcmToken();
        if (token !== storedToken) {
            await _subscribeWithBackend(token);
            saveFcmToken(token);
        }
        return token;
    } catch (error) {
        return null;
    }
};

/**
 * Unsubscribe the stored FCM token from push notifications via the backend,
 * then clear it locally. Called on user-initiated logout.
 */
export const unsubscribeFromPushNotifications = async () => {
    const token = getFcmToken();
    if (!token) {
        return;
    }
    try {
        await pnUnsubscribe(token);
    } finally {
        clearFcmToken();
    }
};

/**
 * Internal: send FCM token to backend's pn/subscribe endpoint.
 */
const _subscribeWithBackend = async (token) => {
    try {
        await pnSubscribe(token);
    } catch (error) {
        // ignore
    }
};

/**
 * Internal: send FCM token to backend's pn/unsubscribe endpoint.
 */
const _unsubscribeFromBackend = async (token) => {
    try {
        await pnUnsubscribe(token);
    } catch (error) {
        // ignore
    }
};

/**
 * Listen for foreground messages. Returns an unsubscribe function.
 * @param {Function} callback - called with the FCM payload
 */
export const onMessageListener = (callback) => {
    return onMessage(messaging, (payload) => {
        if (callback) callback(payload);
    });
};

/**
 * Check if push notifications are supported in this browser.
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
    return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export const getNotificationPermission = () => {
    if (!isNotificationSupported()) return 'unsupported';
    return Notification.permission;
};

export default messaging;
